import React, { useRef, useState } from 'react'
import { ProjectionSpecification } from 'mapbox-gl'
import Map, { MapProps as MapBoxProps, MapRef } from 'react-map-gl'

export type MapRefType = React.MutableRefObject<MapRef | undefined>
// Extend MapBoxProps, omitting 'projection' and 'logoPosition' to handle type incompatibilities
export type MapProps = Omit<MapBoxProps, 'projection' | 'logoPosition'> & {
  // Optional mapRef for direct mapbox-gl map object access
  mapRef?: MapRefType

  // Re-adding 'projection' with its proper type from mapbox-gl
  // This resolves a type incompatibility between react-map-gl and mapbox-gl.
  projection?: ProjectionSpecification

  // Re-adding 'logoPosition' with a narrowed type
  // This resolves a similar type mismatch where mapbox-gl defines broader types
  // than what react-map-gl expects.
  logoPosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
}

const MapBoxMap: React.FC<MapProps> = ({
  children,
  mapRef: externalMapRef,
  ...props
}) => {
  // If we pass in a mapRef, we have direct access to the mapbox-gl map object for full flexibility
  const internalMapRef = useRef<MapRef>(null)
  const mapRef = externalMapRef || internalMapRef
  /*
   * Prevents race conditions by ensuring map is fully loaded before rendering children.
   * This is critical because child elements (polygons, markers, etc.) require the map
   * instance to be fully initialized before they can be properly rendered.
   * If removed in some instances, we get this crash -> ERROR: Style is not done loading
   */
  const [isMapLoaded, setIsMapLoaded] = useState(false)

  const onLoad = (e) => {
    if (props?.onLoad) {
      props.onLoad(e)
    }
    // If we don't pass an initial viewport the map is black
    // This does a small zoom which fixes the issue
    if (!props?.initialViewState) {
      mapRef.current.zoomTo(mapRef.current.getZoom() - 0.001)
    }

    setIsMapLoaded(true)
  }

  const mapboxProps: MapProps = {
    mapboxAccessToken: Project.mapBox.web,
    // Makes the map fill the parent container
    style: { width: '100%', height: '100%', borderRadius: 5 },
    ...props,
    onLoad: onLoad,
  }

  return (
    <Map ref={mapRef} mapLib={import('mapbox-gl')} {...mapboxProps}>
      {isMapLoaded && children}
    </Map>
  )
}

export { MapBoxMap }
